import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useOperator } from "@/context/OperatorContext";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileUpload, AuthImage } from "@/components/FileUpload";
import DayRoutePanel from "@/components/DayRoutePanel";
import { Bike, Plus, Loader2, ArrowLeft, IndianRupee, AlertTriangle, CheckCircle2, Bell, QrCode, ShieldCheck, ChevronDown, Truck, KeyRound, BatteryCharging } from "lucide-react";
import { toast } from "sonner";

const inr = (v) => `₹${(v || 0).toLocaleString("en-IN")}`;
const d = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const STATUS_CLS = {
  active: "bg-status-available/15 text-status-available", onboarding: "bg-status-pending/15 text-status-pending",
  suspended: "bg-status-overdue/15 text-status-overdue", surrendered: "bg-status-recovery/15 text-status-recovery",
  returned: "bg-muted text-muted-foreground",
};

function OnboardDialog({ config, onDone }) {
  const empty = {
    name: "", phone: "", email: "", plan_id: "day_office", password: "",
    kyc: { selfie_file: null, dl_file: null, dl_number: "", aadhaar_file: null, aadhaar_last4: "", platform_screenshots: [], platforms: [] },
    reference: { name: "", phone: "", relation: "" },
    consent_agreed: false, consent_signature: "",
  };
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [showTerms, setShowTerms] = useState(false);
  const [busy, setBusy] = useState(false);

  const setKyc = (patch) => setF((s) => ({ ...s, kyc: { ...s.kyc, ...patch } }));
  const togglePlatform = (p) => setKyc({ platforms: f.kyc.platforms.includes(p) ? f.kyc.platforms.filter((x) => x !== p) : [...f.kyc.platforms, p] });

  const submit = async () => {
    if (!f.name || !f.phone) return toast.error("Name and phone are required");
    if (!f.reference.name || !f.reference.phone) return toast.error("Reference contact is required");
    if (!f.consent_agreed) return toast.error("Rider must accept the contract & consent");
    setBusy(true);
    try {
      await api.post("/operator/delivery/riders", { ...f, consent_signature: f.consent_signature || f.name });
      toast.success("Rider onboarded");
      setOpen(false); setF(empty); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const plan = config.plan_catalog[f.plan_id] || {};
  const deposit = plan.deposit ? config.deposit_long_term : 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="onboard-rider-btn" className="gap-1.5"><Plus className="h-4 w-4" /> Onboard rider</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle>Onboard delivery rider</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Full name</Label><Input data-testid="rider-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="rider-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Email (optional)</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Portal password (optional)</Label><Input data-testid="rider-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="for rider app login" /></div>
          </div>

          <div className="space-y-1.5">
            <Label>Plan</Label>
            <Select value={f.plan_id} onValueChange={(v) => setF({ ...f, plan_id: v })}>
              <SelectTrigger data-testid="rider-plan"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(config.plan_catalog).map(([id, p]) => (
                  <SelectItem key={id} value={id}>{p.label} — {inr(config.plans[id] ?? p.price)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Deposit: <span className="font-medium">{inr(deposit)}</span> {deposit ? "(long-term)" : "(no deposit for daily)"}</p>
          </div>

          <div className="rounded-tenant-card border border-tenant-border p-3">
            <p className="mb-2 text-sm font-semibold">KYC documents</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <FileUpload testid="upload-selfie" label="Selfie" category="kyc" value={f.kyc.selfie_file} onChange={(id) => setKyc({ selfie_file: id })} capture="user" />
              <FileUpload testid="upload-dl" label="Driving licence" category="kyc" value={f.kyc.dl_file} onChange={(id) => setKyc({ dl_file: id })} />
              <FileUpload testid="upload-aadhaar" label="Aadhaar" category="kyc" value={f.kyc.aadhaar_file} onChange={(id) => setKyc({ aadhaar_file: id })} />
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Input placeholder="DL number" value={f.kyc.dl_number} onChange={(e) => setKyc({ dl_number: e.target.value })} />
              <Input placeholder="Aadhaar last 4" maxLength={4} value={f.kyc.aadhaar_last4} onChange={(e) => setKyc({ aadhaar_last4: e.target.value })} />
            </div>
            <p className="mb-1 mt-3 text-xs font-medium text-muted-foreground">Gig platforms they work on</p>
            <div className="flex flex-wrap gap-1.5">
              {config.platforms.map((p) => (
                <button key={p} type="button" data-testid={`platform-${p}`} onClick={() => togglePlatform(p)}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${f.kyc.platforms.includes(p) ? "border-tenant-primary bg-tenant-primary/10 text-tenant-primary" : "border-tenant-border text-muted-foreground"}`}>{p}</button>
              ))}
            </div>
          </div>

          <div className="rounded-tenant-card border border-tenant-border p-3">
            <p className="mb-2 text-sm font-semibold">Reference contact</p>
            <div className="grid grid-cols-3 gap-2">
              <Input data-testid="ref-name" placeholder="Name" value={f.reference.name} onChange={(e) => setF({ ...f, reference: { ...f.reference, name: e.target.value } })} />
              <Input data-testid="ref-phone" placeholder="Phone" value={f.reference.phone} onChange={(e) => setF({ ...f, reference: { ...f.reference, phone: e.target.value } })} />
              <Input placeholder="Relation" value={f.reference.relation} onChange={(e) => setF({ ...f, reference: { ...f.reference, relation: e.target.value } })} />
            </div>
          </div>

          <div className="rounded-tenant-card border border-tenant-border bg-tenant-bg p-3">
            <label className="flex items-start gap-2 text-sm">
              <Checkbox data-testid="consent-checkbox" checked={f.consent_agreed} onCheckedChange={(v) => setF({ ...f, consent_agreed: !!v })} className="mt-0.5" />
              <span>Rider accepts the rental contract, daily QR check-in, idle-day & damage penalties, and consents to KYC storage.
                <button type="button" onClick={() => setShowTerms((s) => !s)} className="ml-1 inline-flex items-center gap-0.5 text-tenant-primary hover:underline">full terms <ChevronDown className={`h-3 w-3 transition-transform ${showTerms ? "rotate-180" : ""}`} /></button>
              </span>
            </label>
            {showTerms && (
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                {config.contract_terms.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            )}
            {f.consent_agreed && (
              <Input data-testid="consent-signature" className="mt-2" placeholder="Type rider's full name as signature" value={f.consent_signature} onChange={(e) => setF({ ...f, consent_signature: e.target.value })} />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="submit-onboard" onClick={submit} disabled={busy} className="gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Onboard rider</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HandoverDialog({ rider, vehicles, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ vehicle_id: "", vehicle_model: "", vehicle_number: "", battery_number: "", charger_number: "", condition_photos: [], notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.vehicle_id) return toast.error("Select a vehicle");
    setBusy(true);
    try { await api.post(`/operator/delivery/riders/${rider.id}/handover`, f); toast.success("Vehicle handed over"); setOpen(false); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const avail = vehicles.filter((v) => v.status === "available");
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="handover-btn" className="gap-1.5"><Truck className="h-4 w-4" /> Hand over vehicle</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Hand over — out condition</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={f.vehicle_id} onValueChange={(v) => { const veh = vehicles.find((x) => x.id === v); setF({ ...f, vehicle_id: v, vehicle_model: veh?.model || "", vehicle_number: veh?.reg_number || "" }); }}>
            <SelectTrigger data-testid="handover-vehicle"><SelectValue placeholder="Select available vehicle" /></SelectTrigger>
            <SelectContent>{avail.map((v) => <SelectItem key={v.id} value={v.id}>{v.reg_number} · {v.model}</SelectItem>)}</SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Vehicle number" value={f.vehicle_number} onChange={(e) => setF({ ...f, vehicle_number: e.target.value })} />
            <Input placeholder="Model" value={f.vehicle_model} onChange={(e) => setF({ ...f, vehicle_model: e.target.value })} />
            <Input data-testid="battery-number" placeholder="Battery number" value={f.battery_number} onChange={(e) => setF({ ...f, battery_number: e.target.value })} />
            <Input data-testid="charger-number" placeholder="Charger number" value={f.charger_number} onChange={(e) => setF({ ...f, charger_number: e.target.value })} />
          </div>
          <FileUpload testid="handover-photo" label="Condition photo" category="condition" value={f.condition_photos[0]} onChange={(id) => setF({ ...f, condition_photos: [id] })} />
          <Input placeholder="Notes (scratches, accessories…)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        </div>
        <DialogFooter><Button data-testid="submit-handover" onClick={submit} disabled={busy} className="gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirm handover</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReturnDialog({ rider, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ battery_number: "", charger_number: "", condition_photos: [], condition_ok: true, damage_charges: 0, damage_notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/operator/delivery/riders/${rider.id}/return`, { ...f, damage_charges: Number(f.damage_charges) || 0 }); toast.success("Vehicle returned & settled"); setOpen(false); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="return-btn" variant="outline" className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Return vehicle</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Return — in condition check</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">What went out must come back in the same condition — verify body, battery & charger.</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Battery number" value={f.battery_number} onChange={(e) => setF({ ...f, battery_number: e.target.value })} />
            <Input placeholder="Charger number" value={f.charger_number} onChange={(e) => setF({ ...f, charger_number: e.target.value })} />
          </div>
          <FileUpload testid="return-photo" label="Return condition photo" category="condition" value={f.condition_photos[0]} onChange={(id) => setF({ ...f, condition_photos: [id] })} />
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.condition_ok} onCheckedChange={(v) => setF({ ...f, condition_ok: !!v })} /> Returned in good condition</label>
          {!f.condition_ok && (
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="damage-charges" type="number" placeholder="Damage charges ₹" value={f.damage_charges} onChange={(e) => setF({ ...f, damage_charges: e.target.value })} />
              <Input placeholder="Damage notes" value={f.damage_notes} onChange={(e) => setF({ ...f, damage_notes: e.target.value })} />
            </div>
          )}
        </div>
        <DialogFooter><Button data-testid="submit-return" onClick={submit} disabled={busy} className="gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirm return</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RiderDetail({ id, vehicles, onBack, onChange }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/operator/delivery/riders/${id}`).then(({ data }) => setData(data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const r = data.rider, b = data.billing;
  const refresh = () => { load(); onChange(); };
  const remind = async () => { const { data } = await api.post(`/operator/delivery/riders/${id}/remind`); toast.success(`Reminder sent (WhatsApp ${data.channel_status.whatsapp ? "live" : "dormant"})`); };
  const qrUrl = data.vehicle?.qr_token ? `${window.location.origin}/r/${data.vehicle.qr_token}` : null;

  return (
    <div className="space-y-5">
      <button data-testid="delivery-back" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to riders</button>
      <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">{r.name} <span className={`ml-1 rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_CLS[r.status] || ""}`}>{r.status}</span></h2>
            <p className="text-sm text-muted-foreground">{r.phone} · {data.today.plan_label}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {r.status === "onboarding" && <HandoverDialog rider={r} vehicles={vehicles} onDone={refresh} />}
            {r.status === "active" && <ReturnDialog rider={r} onDone={refresh} />}
            <Button data-testid="remind-btn" variant="outline" onClick={remind} className="gap-1.5"><Bell className="h-4 w-4" /> Remind</Button>
          </div>
        </div>
        {!data.today.checked_in && r.status === "active" && (
          <div data-testid="idle-warning" className="mt-4 flex items-center gap-2 rounded-tenant-button border border-status-overdue/30 bg-status-overdue/10 px-3 py-2 text-sm text-status-overdue">
            <AlertTriangle className="h-4 w-4" /> Not checked in today — idle days are charged ₹100 each.
          </div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft lg:col-span-2">
          <p className="mb-3 text-sm font-semibold">Billing</p>
          <div className="space-y-1.5 text-sm">
            {b.line_items.map((l, i) => <div key={i} className="flex justify-between"><span className="text-muted-foreground">{l.label}</span><span>{inr(l.amount)}</span></div>)}
            <div className="flex justify-between border-t border-tenant-border pt-2 font-semibold"><span>Total</span><span>{inr(b.total)}</span></div>
            <div className="flex justify-between text-xs text-muted-foreground"><span>Deposit held</span><span>{inr(b.deposit_held)} (refundable {inr(b.deposit_refundable)})</span></div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 text-center text-sm">
            <div className="rounded-tenant-button bg-tenant-bg py-2"><p className="text-lg font-bold">{b.days_held}</p><p className="text-xs text-muted-foreground">days held</p></div>
            <div className="rounded-tenant-button bg-tenant-bg py-2"><p className="text-lg font-bold text-status-available">{b.checkin_days}</p><p className="text-xs text-muted-foreground">check-ins</p></div>
            <div className="rounded-tenant-button bg-tenant-bg py-2"><p className={`text-lg font-bold ${b.idle_days ? "text-status-overdue" : ""}`}>{b.idle_days}</p><p className="text-xs text-muted-foreground">idle days</p></div>
          </div>
        </div>
        <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 text-center shadow-tenant-soft">
          <p className="mb-2 flex items-center justify-center gap-1.5 text-sm font-semibold"><QrCode className="h-4 w-4" /> Daily check-in QR</p>
          {qrUrl ? (
            <>
              <div className="mx-auto w-fit rounded-tenant-card border border-tenant-border bg-white p-3"><QRCodeSVG value={qrUrl} size={140} /></div>
              <p className="mt-2 break-all text-[10px] text-muted-foreground">{qrUrl}</p>
            </>
          ) : (
            <p className="py-8 text-sm text-muted-foreground">Hand over a vehicle to generate the daily check-in QR sticker.</p>
          )}
        </div>
      </div>

      {r.handover && (
        <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
          <p className="mb-3 text-sm font-semibold">Handover condition (out)</p>
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div><p className="text-xs text-muted-foreground">Vehicle</p><p className="font-medium">{r.handover.vehicle_number}</p></div>
            <div><p className="text-xs text-muted-foreground">Model</p><p className="font-medium">{r.handover.vehicle_model}</p></div>
            <div><p className="flex items-center gap-1 text-xs text-muted-foreground"><BatteryCharging className="h-3 w-3" /> Battery #</p><p className="font-medium">{r.handover.battery_number || "—"}</p></div>
            <div><p className="flex items-center gap-1 text-xs text-muted-foreground"><KeyRound className="h-3 w-3" /> Charger #</p><p className="font-medium">{r.handover.charger_number || "—"}</p></div>
          </div>
          {r.handover.condition_photos?.[0] && <AuthImage fileId={r.handover.condition_photos[0]} className="mt-3 h-32 rounded-tenant-button object-cover" />}
        </div>
      )}

      {data.vehicle?.id && (
        <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
          <DayRoutePanel vehicleId={data.vehicle.id} title={`Where ${r.name.split(" ")[0]} rode (daily route & activity)`} />
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
          <p className="mb-3 text-sm font-semibold">KYC & references</p>
          <div className="flex flex-wrap gap-2">
            {[["Selfie", r.kyc.selfie_file], ["DL", r.kyc.dl_file], ["Aadhaar", r.kyc.aadhaar_file]].map(([lbl, fid]) => fid ? (
              <div key={lbl} className="text-center"><AuthImage fileId={fid} className="h-20 w-20 rounded-tenant-button object-cover" /><p className="mt-1 text-xs text-muted-foreground">{lbl}</p></div>
            ) : null)}
          </div>
          <div className="mt-3 space-y-1 text-sm">
            <p><span className="text-muted-foreground">Platforms:</span> {r.kyc.platforms?.join(", ") || "—"}</p>
            <p><span className="text-muted-foreground">DL:</span> {r.kyc.dl_number || "—"} · <span className="text-muted-foreground">Aadhaar:</span> ••••{r.kyc.aadhaar_last4 || "----"}</p>
            <p><span className="text-muted-foreground">Reference:</span> {r.reference?.name} ({r.reference?.relation}) · {r.reference?.phone}</p>
            <p className="flex items-center gap-1 text-xs text-status-available"><ShieldCheck className="h-3.5 w-3.5" /> Consent {r.consent?.agreed ? `signed by ${r.consent.signature} on ${d(r.consent.agreed_at)}` : "—"}</p>
          </div>
        </div>
        <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
          <p className="mb-3 text-sm font-semibold">Check-in history ({data.checkins.length})</p>
          <div className="max-h-48 space-y-1.5 overflow-y-auto">
            {data.checkins.length === 0 ? <p className="text-sm text-muted-foreground">No check-ins yet.</p> :
              data.checkins.map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-tenant-button border border-tenant-border px-3 py-1.5 text-sm">
                  <span><CheckCircle2 className="mr-1 inline h-3.5 w-3.5 text-status-available" /> {c.date}</span>
                  <span className="text-xs capitalize text-muted-foreground">{c.method}</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OperatorDelivery() {
  const { ctx } = useOperator();
  const [config, setConfig] = useState(null);
  const [riders, setRiders] = useState([]);
  const [open, setOpen] = useState(null);

  const loadRiders = useCallback(() => api.get("/operator/delivery/riders").then(({ data }) => setRiders(data)), []);
  useEffect(() => {
    api.get("/operator/delivery/config").then(({ data }) => setConfig(data));
    loadRiders();
  }, [loadRiders]);

  if (!config) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  if (open) return <RiderDetail id={open} vehicles={ctx?.vehicles || []} onBack={() => { setOpen(null); loadRiders(); }} onChange={loadRiders} />;

  const active = riders.filter((r) => r.status === "active").length;
  const idleToday = riders.filter((r) => r.status === "active" && !r.checkin_today).length;

  return (
    <div data-testid="delivery-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Delivery riders</h1>
          <p className="mt-1 text-sm text-muted-foreground">Gig riders on daily QR check-in. {active} active · {idleToday} not checked in today.</p>
        </div>
        <OnboardDialog config={config} onDone={loadRiders} />
      </div>

      {riders.length === 0 ? (
        <div className="rounded-tenant-card border border-dashed border-tenant-border p-12 text-center">
          <Bike className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">No delivery riders yet. Onboard your first gig rider.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {riders.map((r) => (
            <button key={r.id} data-testid={`rider-card-${r.id}`} onClick={() => setOpen(r.id)}
              className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-4 text-left shadow-tenant-soft transition-all hover:-translate-y-0.5 hover:shadow-tenant-hover">
              <div className="flex items-center justify-between">
                <p className="font-semibold">{r.name}</p>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLS[r.status] || ""}`}>{r.status}</span>
              </div>
              <p className="text-sm text-muted-foreground">{r.phone}</p>
              <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                <span>{config.plan_catalog[r.plan_id]?.cycle || ""} plan</span>
                {r.status === "active" && (r.checkin_today
                  ? <span className="flex items-center gap-1 text-status-available"><CheckCircle2 className="h-3.5 w-3.5" /> Checked in</span>
                  : <span className="flex items-center gap-1 text-status-overdue"><AlertTriangle className="h-3.5 w-3.5" /> Idle today</span>)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
